import React, { Suspense, useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import api from "../utils/api";
import {
  clearImpersonation,
  clearSession,
  getImpersonation,
} from "../utils/session";
import AuroraBackground from "../components/AuroraBackground";
import ImpersonationBanner from "../components/ImpersonationBanner";
import useIsMobile from "./useIsMobile";
import PageFallback from "./PageFallback";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";

export default function MainLayout() {
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebar_collapsed") === "true",
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [impersonation, setImpersonationState] = useState(() =>
    getImpersonation(),
  );
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  const handleCollapse = (val) => {
    setCollapsed(val);
    localStorage.setItem("sidebar_collapsed", val);
  };

  const handleLogout = async () => {
    try {
      await api.get("/api/v1/users/logout");
    } catch {
      /* Logout should continue even if the network request fails. */
    }
    clearSession();
    clearImpersonation();
    navigate("/login");
  };

  const handleExitImpersonation = () => {
    setImpersonationState(null);
    navigate("/tenants");
  };

  return (
    <div
      style={{
        display: "flex",
        height: "100%",
        overflow: "hidden",
        background: "var(--page-bg)",
        position: "relative",
      }}
    >
      <AuroraBackground />
      {impersonation && (
        <ImpersonationBanner
          tenantName={impersonation.tenantName}
          userName={impersonation.userName}
          userRole={impersonation.userRole}
          onExit={handleExitImpersonation}
        />
      )}

      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "var(--overlay-soft)",
            zIndex: 199,
            backdropFilter: "blur(2px)",
          }}
        />
      )}

      <Sidebar
        collapsed={collapsed}
        setCollapsed={handleCollapse}
        onLogout={handleLogout}
        isMobile={isMobile}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          position: "relative",
          zIndex: 1,
          paddingTop: impersonation ? (isMobile ? 56 : 37) : 0,
        }}
      >
        <TopBar
          isMobile={isMobile}
          onMenuToggle={() => setMobileOpen((open) => !open)}
        />
        <main
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            padding: isMobile ? "20px 16px" : "32px 36px",
          }}
        >
          <Suspense fallback={<PageFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
